import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Check, AlertCircle, Loader2, Send } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const RescheduleSelect = () => {
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestDate, setSuggestDate] = useState('');
  const [suggestTime, setSuggestTime] = useState('');
  const [suggestSent, setSuggestSent] = useState(false);
  
  // Get token from URL
  const pathParts = window.location.pathname.split('/');
  const token = pathParts[pathParts.length - 1];
  
  // Get pre-selected slot or suggest mode from URL query
  const urlParams = new URLSearchParams(window.location.search);
  const preSelectedSlot = urlParams.get('slot');
  const suggestMode = urlParams.get('suggest') === 'true';

  useEffect(() => {
    fetchOffer();
  }, []);

  useEffect(() => {
    if (suggestMode) {
      setShowSuggest(true);
    }
  }, [suggestMode]);

  useEffect(() => {
    // Auto-select if slot was passed in URL (from email click)
    if (offer && offer.status === 'pending' && preSelectedSlot !== null && !suggestMode) {
      const slotIndex = parseInt(preSelectedSlot);
      if (!isNaN(slotIndex) && slotIndex >= 0 && slotIndex < offer.slot_options.length) {
        handleSelect(slotIndex);
      }
    }
  }, [offer, preSelectedSlot]);

  const fetchOffer = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reschedule-offer/${token}`);
      setOffer(response.data);
      
      if (response.data.status === 'expired') {
        setError('This reschedule offer has expired. Please contact the studio.');
      } else if (response.data.status === 'already_selected') {
        setSuccess({
          date: response.data.selected_date,
          time: response.data.selected_time
        });
      }
    } catch (err) {
      console.error('Error fetching offer:', err);
      setError('Unable to load reschedule options. Please contact the studio.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (index) => {
    if (selecting) return;
    
    setSelecting(true);
    setSelectedIndex(index);
    
    try {
      const response = await axios.post(`${API}/reschedule-offer/${token}/select`, {
        slot_index: index
      });
      
      setSuccess({
        date: response.data.new_date,
        time: response.data.new_time
      });
    } catch (err) {
      console.error('Error selecting slot:', err);
      setError(err.response?.data?.detail || 'Failed to select time slot. Please try again.');
      setSelectedIndex(null);
    } finally {
      setSelecting(false);
    }
  };

  const handleSuggestTime = async () => {
    if (!suggestDate || !suggestTime) {
      return;
    }
    
    setSelecting(true);
    
    try {
      await axios.post(`${API}/reschedule-offer/${token}/suggest`, {
        suggested_date: suggestDate,
        suggested_time: suggestTime
      });
      
      setSuggestSent(true);
    } catch (err) {
      console.error('Error suggesting time:', err);
      if (err.response?.data?.detail?.includes('not available')) {
        setError('Sorry, that time slot is not available. Please choose from the offered times or try a different time.');
      } else {
        setError(err.response?.data?.detail || 'Failed to submit suggestion. Please try again.');
      }
    } finally {
      setSelecting(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-black flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading options...</p>
        </div>
      </div>
    );
  }

  if (suggestSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-8 border border-blue-500/30 text-center">
          <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Send className="w-10 h-10 text-blue-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">Request Sent!</h1>
          <p className="text-gray-400 mb-6">
            We've received your preferred time. The studio will review and confirm if it's available.
          </p>
          
          <div className="bg-black/40 rounded-xl p-4 mb-6">
            <p className="text-gray-400 text-sm">Requested time:</p>
            <p className="text-white font-semibold">{formatDate(suggestDate)} at {suggestTime}</p>
          </div>
          
          <p className="text-gray-500 text-sm">
            You'll receive a confirmation email shortly.
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-8 border border-green-500/30 text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">Session Rescheduled!</h1>
          <p className="text-gray-400 mb-6">Your new session time has been confirmed.</p>
          
          <div className="bg-black/40 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-white mb-2">
              <Calendar className="w-5 h-5 text-purple-400" />
              <span className="font-semibold">{formatDate(success.date)}</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-white">
              <Clock className="w-5 h-5 text-purple-400" />
              <span className="font-semibold">{success.time}</span>
            </div>
          </div>
          
          <p className="text-gray-500 text-sm">
            A confirmation email has been sent to you.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-8 border border-red-500/30 text-center">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">Oops!</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          
          <a 
            href="/"
            className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
          >
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }

  if (!offer || offer.status !== 'pending') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-black flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">High Quality Studios</h1>
          <p className="text-gray-400">Select your new session time</p>
        </div>
        
        {/* Main Card */}
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-6 border border-purple-500/30">
          {/* Greeting */}
          <div className="mb-6">
            <p className="text-gray-400">Hi <span className="text-white font-semibold">{offer.customer_name}</span>,</p>
            <p className="text-gray-400 mt-2">
              Your session originally scheduled for{' '}
              <span className="text-red-400 line-through">{formatDate(offer.original_date)} at {offer.original_time}</span>
              {' '}needs to be rescheduled.
            </p>
          </div>
          
          {/* Engineer */}
          <div className="bg-black/30 rounded-lg p-3 mb-6">
            <p className="text-gray-400 text-sm">
              Engineer: <span className="text-purple-400 font-semibold">{offer.engineer_name}</span>
            </p>
          </div>
          
          {showSuggest ? (
            /* Suggest Your Own Time */
            <div className="mb-6">
              <p className="text-gray-300 font-semibold mb-3">Suggest your preferred time:</p>
              
              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-sm block mb-1">Date</label>
                  <input
                    type="date"
                    value={suggestDate}
                    onChange={(e) => setSuggestDate(e.target.value)}
                    className="w-full px-4 py-3 bg-black/40 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
                
                <div>
                  <label className="text-gray-400 text-sm block mb-1">Time</label>
                  <select
                    value={suggestTime}
                    onChange={(e) => setSuggestTime(e.target.value)}
                    className="w-full px-4 py-3 bg-black/40 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Select a time</option>
                    {['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM', '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                
                <button
                  onClick={handleSuggestTime}
                  disabled={selecting || !suggestDate || !suggestTime}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50"
                >
                  {selecting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Submit Request'}
                </button>
                
                <button
                  onClick={() => setShowSuggest(false)}
                  className="w-full py-2 text-gray-400 hover:text-white text-sm"
                >
                  ← Back to offered times
                </button>
              </div>
            </div>
          ) : (
            /* Slot Options */
            <div className="mb-6">
              <p className="text-gray-300 font-semibold mb-3">Choose a new time:</p>
              
              <div className="space-y-3">
                {offer.slot_options.map((slot, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelect(index)}
                    disabled={selecting}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      selectedIndex === index
                        ? 'border-purple-500 bg-purple-500/20'
                        : 'border-gray-700 bg-black/30 hover:border-purple-500/50 hover:bg-black/50'
                    } ${selecting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-white font-semibold">
                          <Calendar className="w-4 h-4 text-purple-400" />
                          {formatDate(slot.date)}
                        </div>
                        <div className="flex items-center gap-2 text-gray-400 mt-1">
                          <Clock className="w-4 h-4 text-purple-400" />
                          {slot.time}
                        </div>
                      </div>
                      
                      {selectedIndex === index && selecting ? (
                        <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                      ) : (
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selectedIndex === index ? 'border-purple-500 bg-purple-500' : 'border-gray-600'
                        }`}>
                          {selectedIndex === index && <Check className="w-4 h-4 text-white" />}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              
              <div className="text-center my-4">
                <span className="text-gray-600">— or —</span>
              </div>
              
              <button
                onClick={() => setShowSuggest(true)}
                className="w-full py-3 border-2 border-purple-500/50 text-purple-400 rounded-xl font-semibold hover:bg-purple-500/10 transition-all"
              >
                ✏️ Suggest a Different Time
              </button>
            </div>
          )}
          
          {/* Expiry Notice */}
          {!showSuggest && (
            <p className="text-center text-gray-500 text-xs">
              ⏰ Please select within the next hour or these slots may become unavailable.
            </p>
          )}
        </div>
        
        {/* Footer */}
        <p className="text-center text-gray-600 text-xs mt-6">
          Questions? Contact us at trapspade@hqstu.com
        </p>
      </div>
    </div>
  );
};

export default RescheduleSelect;
